import React, { useState, useEffect, useRef } from 'react';
import "./DatePickerWheel.css"

export const DOBPickerModal = ({ isOpen, onClose, onSelectDate, initialDate }) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const lastSelectableYear = currentYear - 8;
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const [day, setDay] = useState(today.getDate());
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(lastSelectableYear);
  const [days, setDays] = useState(() => {
    const totalDays = getDaysInMonth(today.getMonth(), lastSelectableYear);
    return Array.from({ length: totalDays }, (_, i) => i + 1);
  });

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'
  ];
  const currentDate = new Date();
  const maxYear = currentDate.getFullYear() - 7;
  const minYear = 1900;

  const years = [];
  for (let i = maxYear; i >= minYear; i--) {
    years.push(i);
  }

  useEffect(() => {
    if (dayRef.current && days.length > 0) {
      const itemHeight = 40;
      dayRef.current.scrollTop = (day - 1) * itemHeight; // scroll to current day
    }
  }, [days, day]);

  useEffect(() => {
    const totalDays = getDaysInMonth(month, year);
    const newDays = Array.from({ length: totalDays }, (_, i) => i + 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    if (day > daysInMonth) {
      setDay(daysInMonth);
    }

    setDays(newDays);
  }, [month, year]);

  const scrollTimeouts = useRef({});

  const handleScroll = (ref, setter, values, key) => {
    const element = ref.current;
    if (!element) return;

    if (scrollTimeouts.current[key]) {
      clearTimeout(scrollTimeouts.current[key]);
    }

    const scrollTop = element.scrollTop;
    const itemHeight = 40;
    const index = Math.round(scrollTop / itemHeight);
    const clampedIndex = Math.max(0, Math.min(index, values.length - 1));

    setter(values[clampedIndex]);

    scrollTimeouts.current[key] = setTimeout(() => {
      element.scrollTo({
        top: clampedIndex * itemHeight,
        behavior: "smooth"
      });
    }, 150);
  };

  const scrollToValue = (ref, value, values) => {
    const element = ref.current;
    if (!element) return;

    const index = values.indexOf(value);
    if (index !== -1) {
      element.scrollTop = index * 40;
    }
  };

  // Handle click to select
  const handleItemClick = (ref, setter, value, values) => {
    setter(value);
    const element = ref.current;
    if (!element) return;

    const index = values.indexOf(value);
    if (index !== -1) {
      element.scrollTo({
        top: index * 40,
        behavior: "smooth"
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (initialDate) {
        const date = new Date(initialDate);
        setMonth(date.getMonth());
        setYear(date.getFullYear());
      }

      setTimeout(() => {
        scrollToValue(dayRef, day, days);
        scrollToValue(monthRef, month, Array.from({ length: 12 }, (_, i) => i));
        scrollToValue(yearRef, year, years);
      }, 100);
    }
  }, [isOpen]);

  const handleResetDate = () => {
    const today = new Date();
    const sevenYearsAgo = new Date(today.getFullYear() - 7, today.getMonth(), today.getDate());

    setMonth(sevenYearsAgo.getMonth());
    setYear(sevenYearsAgo.getFullYear());

    setTimeout(() => {
      scrollToValue(dayRef, sevenYearsAgo.getDate(), days);
      scrollToValue(monthRef, sevenYearsAgo.getMonth(), Array.from({ length: 12 }, (_, i) => i));
      scrollToValue(yearRef, sevenYearsAgo.getFullYear(), years);
    }, 0);
  };

  const handleOverlayClick = () => {
    onClose && onClose();
  };

  if (!isOpen) return null;

  const handleConfirm = () => {
    const selectedDateString = `${day} ${months[month]} ${year}`;
    const dateObject = new Date(year, month, day);
    onSelectDate && onSelectDate(selectedDateString, dateObject);
    onClose && onClose();
  };

  return (
    <>
      <div className="dob-modal-overlay" onClick={handleOverlayClick} />
      <div className="dob-modal-container">
        <div className="dob-modal-body">
          <div className="dob-selection-highlight">
            <div className="dob-selection-highlight-inner" />
          </div>

          <div className="dob-picker-container ">

            <div className="dob-column">
              <div ref={dayRef} className="dob-scroll-container"
                onScroll={() => handleScroll(dayRef, setDay, days, 'day')}
                onWheel={(e) => {
                  e.preventDefault();
                  const delta = e.deltaY > 0 ? 3 : -3;
                  dayRef.current.scrollTop += delta;
                }}
              >
                <div className="dob-spacer" />
                {days.map((d) => (
                  <div
                    key={d}
                    className={`dob-item ${d === day ? 'active' : ''}`}
                    onClick={() => handleItemClick(dayRef, setDay, d, days)}
                    style={{ cursor: 'pointer' }}
                  >
                    {d}
                  </div>
                ))}
                <div className="dob-spacer" />
              </div>
            </div>

            <div className="dob-column">
              <div
                ref={monthRef}
                className="dob-scroll-container"
                onScroll={() => handleScroll(monthRef, setMonth, Array.from({ length: 12 }, (_, i) => i), 'month')}
                onWheel={(e) => {
                  e.preventDefault();
                  const delta = e.deltaY > 0 ? 10 : -10;
                  monthRef.current.scrollTop += delta;
                }}
              >
                <div className="dob-spacer" />
                {months.map((m, idx) => (
                  <div
                    key={idx}
                    className={`dob-item ${idx === month ? 'active' : ''}`}
                    onClick={() => handleItemClick(monthRef, setMonth, idx, Array.from({ length: 12 }, (_, i) => i))}
                    style={{ cursor: 'pointer' }}
                  >
                    {m}
                  </div>
                ))}
                <div className="dob-spacer" />
              </div>
            </div>

            <div className="dob-column">
              <div
                ref={yearRef}
                className="dob-scroll-container"
                onScroll={() => handleScroll(yearRef, setYear, years, 'year')}
                onWheel={(e) => {
                  e.preventDefault();
                  const delta = e.deltaY > 0 ? 10 : -10;
                  yearRef.current.scrollTop += delta;
                }}
              >
                <div className="dob-spacer" />
                {years.map((y) => (
                  <div
                    key={y}
                    className={`dob-item ${y === year ? 'active' : ''}`}
                    onClick={() => handleItemClick(yearRef, setYear, y, years)}
                    style={{ cursor: 'pointer' }}
                  >
                    {y}
                  </div>
                ))}
                <div className="dob-spacer" />
              </div>
            </div>
          </div>
        </div>

        <div className="dob-modal-footer">
          <button className="dob-cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button className="dob-reset-button " onClick={handleConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </>
  );
};